'use client'
import LoadingPage from '@/app/[locale]/projects/[id]/loading'
import { FeatureFlag, ShareModal, globalConfig } from '@repo/shared'
import { useProject } from '@repo/shared/hooks'
import { type ProjectsProject } from '@repo/shared/types'
import { Button, Col, Icon, Row, SectionSeparator } from '@repo/ui/components'
import { useIsDesktop } from '@repo/ui/hooks'
import dynamic from 'next/dynamic'
import { notFound, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { ProjectDetailHeader } from './projects-detail.header'
import { ProjectDetailInfo } from './projects-detail.info'
import { ProjectDetailPriceChart } from './projects-detail.price-chart'
import { ProjectDetailsSidebar } from './projects-detail.sidebar'
import { ProjectsDetailTradeAction } from './projects-detail.trade-action'
import { useGetReferralCodes } from '@repo/services'
import { useTranslate } from '@repo/shared/hooks'

const ProjectsDetailCTA = dynamic(
  () =>
    import('@/containers/projects/details/projects-detail.cta').then((mod) => ({
      default: mod.ProjectsDetailCTA,
    })),
  { ssr: true },
)

export interface ProjectDetailsContainerProps {
  projectId: string
}

export const ProjectDetailsContainer = ({
  projectId,
}: ProjectDetailsContainerProps) => {
  const { project, projectData, isLoading, error } = useProject(projectId)
  const { t } = useTranslate()
  const isDesktop = useIsDesktop()
  const searchParams = useSearchParams()
  const router = useRouter()
  const [tab, setTab] = useState('detail')
  const tabFromQuery = useMemo(() => searchParams.get('tab'), [searchParams])

  const { data } = useGetReferralCodes()
  const referralCode = data?.data?.codes?.[0]?.code
  const shareUrl = `${globalConfig.baseUrl}/rwa/${projectData.token}/ref/${referralCode}`
  const shareText = t('projectsPage_share_text', {
    name: projectData.displayName ?? '',
    url: shareUrl,
  })

  // Read tab from query string on component mount
  useEffect(() => {
    if (isDesktop) {
      setTab('detail')
      router.push(`${window.location.pathname}`, { scroll: false })
    } else {
      if (tabFromQuery && ['detail', 'trade'].includes(tabFromQuery)) {
        setTab(tabFromQuery)
      }
    }
  }, [isDesktop, router, tabFromQuery])

  if (isLoading) {
    return <LoadingPage />
  }

  if (error || !project || !projectData) {
    return notFound()
  }

  const handleChangeTab = (value: string) => {
    setTab(value)

    // Update URL with new tab parameter
    const current = new URLSearchParams(Array.from(searchParams.entries()))
    if (value === 'detail') {
      current.delete('tab')
    } else {
      current.set('tab', value)
    }

    const search = current.toString()
    const query = search ? `?${search}` : ''
    router.push(`${window.location.pathname}${query}`, { scroll: false })
  }

  return (
    <>
      {!isDesktop && !!referralCode && (
        <ProjectsDetailCTA onChange={handleChangeTab} tab={tab}>
          <ShareModal url={shareUrl} text={shareText}>
            <Icon
              name='ShareAndroid'
              size='sm'
              color='fill-neutral-surface-100'
            />
          </ShareModal>
        </ProjectsDetailCTA>
      )}
      <div
        className={twMerge(
          tab !== 'trade' ? 'hidden' : '',
          !isDesktop && 'mt-9xl',
        )}
      >
        <ProjectDetailsSidebar project={project as ProjectsProject} />
      </div>
      <Col
        className={twMerge(
          'mx-auto gap-md sm:p-md mb-8xl md:mb-0',
          tab === 'trade' && 'hidden',
          !isDesktop && 'mt-xl',
        )}
      >
        <Row className='justify-end'>
          {!!referralCode && isDesktop && (
            <ShareModal url={shareUrl} text={shareText}>
              <Button
                variant='ghost'
                size='md'
                color='neutral'
                endIcon={
                  <Icon
                    name='ShareAndroid'
                    size='xs'
                    color='fill-neutral-surface-100'
                  />
                }
              >
                {t('referralProgram_shareOn_title')}
              </Button>
            </ShareModal>
          )}
        </Row>

        <Row
          className={`flex flex-col md:flex-row w-full flex-1 mx-auto gap-xs`}
        >
          <Col className='gap-lg mb-md sm:px-md'>
            <div id='token-details'>
              <ProjectDetailHeader project={project as ProjectsProject} />
            </div>
            <SectionSeparator />

            <FeatureFlag flagKey='charts-project-history'>
              <ProjectDetailPriceChart project={project as ProjectsProject} />
              <SectionSeparator />
            </FeatureFlag>
            <ProjectDetailInfo project={project as ProjectsProject} />
            <SectionSeparator />
          </Col>
          {isDesktop && (
            <Col className='w-full md:w-96 p-md md:p-0 md:sticky md:top-7xl md:bottom-0 md:self-start'>
              <ProjectDetailsSidebar project={project as ProjectsProject} />
            </Col>
          )}
        </Row>
      </Col>
      {tab !== 'trade' && !isDesktop && <ProjectsDetailTradeAction />}
    </>
  )
}
